import React, { forwardRef, useState, useEffect } from 'react';
import classNames from 'classnames';
import PropTypes from 'prop-types';

import Button, { ButtonProps } from '@material-ui/core/Button';
import CircularProgress from '@material-ui/core/CircularProgress';

import classes from './WaitingButton.scss';

export type WaitingButtonProps<D extends React.ElementType = 'button'> = ButtonProps<D, {
  waiting: boolean,
  component?: React.ComponentType,
  to?: string,
  delay?: number,
}>;

const DEFAULT_DELAY = 800;

export function WaitingButton<D extends React.ElementType = 'button'>({
  waiting = false,
  delay = DEFAULT_DELAY,
  className = null,
  disabled,
  fullWidth,
  ...rest
}: WaitingButtonProps<D>, ref: React.Ref<HTMLSpanElement>): JSX.Element {
  const [isWaitingVisible, setIsWaitingVisible] = useState<boolean>(waiting);

  useEffect(() => {
    if (waiting) {
      const timeout = setTimeout(() => setIsWaitingVisible(true), delay);

      return () => clearTimeout(timeout);
    }

    setIsWaitingVisible(false);
  }, [waiting, delay]);

  return (
    <span className={classNames(
      classes.container,
      className,
      { [classes.fullWidth]: fullWidth }
    )}>
      <Button
        disabled={waiting || disabled}
        {...rest}
        fullWidth
      />
      {isWaitingVisible ?
        (
          <span className={classes.progressContainer}>
            <CircularProgress size={24} />
          </span>
        ) :
        null
      }
    </span>
  )
}

const WrappedWaitingButton = forwardRef(WaitingButton);

WrappedWaitingButton.propTypes = {
  waiting: PropTypes.bool,
  delay  : PropTypes.number,
};

export default WrappedWaitingButton;
